import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import EditIcon from 'assets/svg/edit-icon-gray.svg';
import defaultImagePlaceholder from 'assets/svg/profile-placeholder.svg';
import { Text1 } from 'components/typography/Texts';
import { LoadingIndicator } from 'components/ui-elements/LoadingIndicator';
import { useUpdateMyProfileDetails } from 'features/my-profile/account-settings/hooks/useUpdateMyProfileDetails';
import { convertToThumbnail300 } from 'utils/thumbnails';

import { UserPhotoUploader } from './UserPhotoUploader';

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const UserImage = styled.img`
  border-radius: 120px;
  width: 240px;
  height: 240px;
  cursor: pointer;
  object-fit: cover;

  ${theme.mq.tablet} {
    width: 180px;
    height: 180px;
  }
`;

const ChangePhotoButton = styled.div`
  cursor: pointer;
  display: flex;
  gap: 5px;
  align-items: center;
`;

const StyledIcon = styled.img`
  height: 18px;
`;

const ImageWrapper = styled.div`
  width: 240px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${theme.mq.tablet} {
    width: 180px;
    height: 180px;
  }
`;

interface UserPhotoProps {
  userImage: string;
}

export const UserPhoto = ({ userImage }: UserPhotoProps) => {
  const { t } = useTranslation('myProfile');
  const inputRef = useRef<HTMLInputElement>(null);
  const { updateMyProfileDetails, isLoading } = useUpdateMyProfileDetails();

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleImageUploaded = (url: string) => {
    updateMyProfileDetails({ imageUrl: url });
  };

  return (
    <ImageContainer data-testid={'account-settings-user-image-container'}>
      <ImageWrapper>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <UserImage
            data-testid={'account-settings-user-image'}
            src={userImage ? convertToThumbnail300(userImage) : defaultImagePlaceholder}
            onClick={handleClick}
          />
        )}
      </ImageWrapper>

      <ChangePhotoButton data-testid={'change-photo-btn'} onClick={handleClick}>
        <StyledIcon src={EditIcon} alt={'edit icon'} />
        <Text1>{userImage ? t('account-settings.change-photo') : t('account-settings.add-photo')}</Text1>
      </ChangePhotoButton>
      <UserPhotoUploader inputRef={inputRef} onImageUploaded={handleImageUploaded} />
    </ImageContainer>
  );
};
